import axios from "axios";
import {Contact} from "@/api/contacts";

export enum ProgressState {
  OPEN = "OPEN",
  IN_PROGRESS = "IN_PROGRESS",
  CLOSED = "CLOSED",
}

export interface EmailAttachment {
  id: string | null;
  name: string | null;
  sizeByte: number | null;
  mimeType: string | null;
}

export interface Email {
  id?: string | null;
  subject: string;
  from?: string;
  recipients: string[];
  cc: string[];
  bcc: string[];
  bodyAsHtml: string;
  receivedAtUtc?: Date;
  contactRef?: string | null;
  assigneeRef?: string | null;
  contactHistoryRef?: string | null;
  contactDocumentRefs: string[] | null;
  predefinedDocumentRefs: string[] | null;
  countAttachments?: number | null;
  progressState?: ProgressState | null;
  answers?: string[] | null;
  taskRefs?: string[] | null;
  linkedTaskRefs?: string[] | null;
  countAnswers?: number | null;
  countTaskRefs?: number | null;
  important?: boolean | null;
  contactFullName?: string | null;
  imageGalleryRefs: string[] | null;
}

interface EmailApi {
  listEmails(): Promise<Email[]>;
  listEmailsByAssigneeRefs(assigneeRefs: string[]): Promise<Email[]>;
  getMessage(emailId: string): Promise<Email>;
  listAttachments(emailId: string): Promise<EmailAttachment[]>;
  refreshAttachments(emailId: string): Promise<void>;
  generateAttachmentDownloadLink(emailId: string, attachmentId: string): string;
  linkToContact(emailId: string, contactId: string): Promise<void>;
  addToEncounter(
    emailId: string,
    contactId: string,
    withAttachment: boolean,
    withComments: boolean
  ): Promise<void>;
  setToInProgress(emailId: string, userid: string | null): Promise<void>;
  setToOpen(emailId: string): Promise<void>;
  setToClose(emailId: string): Promise<void>;
  toggleEmailAsImportant(email: Email): Promise<void>;
  delete(email: Email, id: string): Promise<void>;

  releaseContact(email: Email): Promise<void>;
}

export const emailApi: EmailApi = {
  async listEmails() {
    const result = (await axios.get("/api/emails")).data;
    if (result) {
      return result.map((v: Email) => {
        return {
          ...v,
          receivedAtUtc: v.receivedAtUtc ? new Date(v.receivedAtUtc) : null,
        } as Email;
      });
    }
    return [];
  },
  async listEmailsByAssigneeRefs(assigneeRefs) {
    const params = new URLSearchParams();
    assigneeRefs.forEach((value) => params.append("assigneeRefs", value));
    const request = {
      params: params,
    };
    const result = (await axios.get("/api/emails", request)).data;
    if (result) {
      return result.map((v: Email) => {
        return {
          ...v,
          receivedAtUtc:  v.receivedAtUtc ? new Date(v.receivedAtUtc) : null,
        } as Email;
      });
    }
    return [];
  },
  async getMessage(emailId) {
    const t = (await axios.get(`/api/emails/${emailId}`)).data;
    t.receivedAtUtc = new Date(t.receivedAtUtc);
    return t;
  },

  async listAttachments(emailId) {
    return (await axios.get(`/api/emails/${emailId}/attachments`)).data;
  },

  async refreshAttachments(emailId) {
    await axios.post(`/api/emails/${emailId}/attachments/_refresh`);
  },

  async linkToContact(emailId, contact) {
    return (
      await axios.put(
        `/api/emails/${emailId}/link-to-contact?contactRef=${contact}`
      )
    ).data;
  },

  async addToEncounter(emailId, contact, withAttachment, withComments) {
    return (
      await axios.put(
        `/api/emails/${emailId}/add-to-encounter?contactRef=${contact}&withAttachment=${withAttachment}&withComments=${withComments}`
      )
    ).data;
  },

  generateAttachmentDownloadLink(emailId, attachmentId) {
    return `/api/emails/${emailId}/attachments/${attachmentId}`;
  },

  async setToOpen(emailId) {
    return (await axios.put(`/api/emails/${emailId}/set-to-open`)).data;
  },

  async setToInProgress(emailId, userid) {
    if (userid) {
      return (
        await axios.put(`/api/emails/${emailId}/set-in-progress?userId=${userid}`)
      ).data;
    }
    return (await axios.put(`/api/emails/${emailId}/set-in-progress`)).data;
  },

  async setToClose(emailId) {
    return (await axios.put(`/api/emails/${emailId}/set-to-close`)).data;
  },

  async toggleEmailAsImportant(email) {
    return (await axios.put(`/api/emails/${email.id}/toggle-important`)).data;
  },

  async delete(emailId, mailboxId) {
    await axios.delete(`/api/emails/${emailId}?mailboxId=${mailboxId}`);
  },

  async releaseContact(email) {
    await axios.put(`/api/emails/${email.id}/release-contact`);
  },
};
